// ============================================================
// localDB.js — Offline-first local database using IndexedDB
// ============================================================
// This is the PRIMARY data store. All reads come from here.
// Writes go here first (instant UI), then sync to Supabase.
//
// Tables mirrored locally:
//   transactions, transaction_splits, transaction_receipts
//   payment_accounts, credit_cards
//   assets, liabilities
//   expense_categories, income_sources
//   user_settings
// ============================================================

const DB_NAME    = 'myfinance_local';
const DB_VERSION = 5; // v5: added 'tags' + 'transaction_tags' stores (tags feature) — bumping
                      // this is what actually makes onupgradeneeded create them for devices
                      // that already had a local DB at v4; without a version bump, existing
                      // installs never get these object stores created at all.

const STORES = [
  'transactions',
  'transaction_splits',
  'transaction_receipts',
  'payment_accounts',
  'credit_cards',
  'assets',
  'liabilities',
  'expense_categories',
  'income_sources',
  'user_settings',
  'transaction_tags',
  'asset_valuations',
  'backups_meta',     // backup metadata: id, created_at, label, type, size, tx count — small, fast to list
  'backups_payload',  // backup JSON content, keyed by same id — only fetched on demand (share/restore)
  'tags',
  '_sync_meta',   // tracks last_sync_at per table
];

let _db = null;

export async function getLocalDB() {
  if (_db) return _db;
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      STORES.forEach(name => {
        if (!db.objectStoreNames.contains(name)) {
          // transaction_tags has no `id` column in Supabase — its actual
          // primary key is the composite (transaction_id, tag_id). Every
          // other store's rows carry a real `id`, so only this one needs
          // the array keyPath.
          const keyPath = name === 'transaction_tags' ? ['transaction_id', 'tag_id'] : 'id';
          db.createObjectStore(name, { keyPath });
        }
      });
    };
    req.onsuccess  = e => { _db = e.target.result; resolve(_db); };
    req.onerror    = e => reject(e.target.error);
  });
}

// ── Generic CRUD ──────────────────────────────────────────────────────────────

export async function ldbGetAll(store) {
  const db = await getLocalDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

export async function ldbGet(store, id) {
  const db = await getLocalDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror   = () => reject(req.error);
  });
}

export async function ldbPut(store, record) {
  const db = await getLocalDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).put(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function ldbPutBulk(store, records) {
  if (!records?.length) return;
  const db = await getLocalDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const os = tx.objectStore(store);
    records.forEach(r => os.put(r));
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

export async function ldbDelete(store, id) {
  const db = await getLocalDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

export async function ldbClear(store) {
  const db = await getLocalDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).clear();
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

// ── Sync metadata ─────────────────────────────────────────────────────────────

export async function getLastSync(table) {
  const db = await getLocalDB();
  return new Promise(resolve => {
    const tx  = db.transaction('_sync_meta', 'readonly');
    const req = tx.objectStore('_sync_meta').get(table);
    req.onsuccess = () => resolve(req.result?.last_sync_at || null);
    req.onerror   = () => resolve(null);
  });
}

export async function setLastSync(table, isoTimestamp) {
  const db = await getLocalDB();
  return new Promise(resolve => {
    const tx = db.transaction('_sync_meta', 'readwrite');
    tx.objectStore('_sync_meta').put({ id: table, last_sync_at: isoTimestamp });
    tx.oncomplete = () => resolve();
    tx.onerror    = () => resolve();
  });
}

// ── Query helpers ─────────────────────────────────────────────────────────────

export async function ldbQuery(store, filterFn) {
  const all = await ldbGetAll(store);
  return filterFn ? all.filter(filterFn) : all;
}

// ── Full DB export (for iCloud backup) ───────────────────────────────────────

export async function exportFullDB() {
  const result = {};
  const EXCLUDE = ['_sync_meta', 'backups_meta', 'backups_payload'];
  for (const store of STORES) {
    if (EXCLUDE.includes(store)) continue;
    result[store] = await ldbGetAll(store);
  }
  return result;
}

// ── Full DB import (restore from backup) ─────────────────────────────────────

export async function importFullDB(data) {
  const EXCLUDE = ['_sync_meta', 'backups_meta', 'backups_payload'];
  for (const [store, records] of Object.entries(data)) {
    if (!STORES.includes(store) || EXCLUDE.includes(store)) continue;
    await ldbClear(store);
    if (records?.length) await ldbPutBulk(store, records);
  }
}

// ── Clear everything (on logout) ─────────────────────────────────────────────

export async function clearLocalDB() {
  for (const store of STORES) {
    await ldbClear(store);
  }
}
